import { useLoadingState } from '@ipfabric/ui-components/hooks/useLoadingState';
import { withLoadingIndicator } from '@ipfabric/ui-components/utils/loading/withLoadingIndicator';
import type { ApiTableModel } from 'components/ApiTable/model/ApiTableModel';
import { reloadTableData } from 'components/ApiTable/model/commands/reloadTableData';
import {
  type UseApiTableProps,
  useApiTable,
} from 'components/ApiTable/model/useApiTable';
import { useSimpleConfirmDialog } from 'containers/ConfirmDialog/useSimpleConfirmDialog';
import {
  type ReactNode,
  type RefObject,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { useApi } from 'utils/request/useApi';
import { useTableEndpoint } from 'utils/table/useTableEndpoint';
import { useResponsiveTableButton } from '../../../utils/useResponsiveTableButton';

type UseDeviceSnapshotAttributesTableProps = Partial<UseApiTableProps> & {
  snapshotId: string;
  containerRef: RefObject<HTMLElement | null>;
  onUpdated?: () => void;
};

export const useSnapshotAttributesTable = (
  props: UseDeviceSnapshotAttributesTableProps,
): {
  model: ApiTableModel;
  renderConfirmDialog: () => ReactNode;
} => {
  const api = useApi();
  const [updatingFromGlobal, setUpdatingFromGlobal] = useLoadingState();
  const confirmUpdateFromGlobalDialog = useSimpleConfirmDialog();
  const reloadTableDataRef = useRef<(() => void) | undefined>(undefined);

  const willGlobalUpdateNeedRecalculation = async () => {
    const { needsRecalculation } = await api.request<
      { needsRecalculation: boolean },
      { snapshot: string }
    >({
      url: '/attributes/local/update-from-global/check-sites-recalculation',
      method: 'POST',
      data: { snapshot: props.snapshotId },
    });
    return needsRecalculation;
  };

  const updateFromGlobalSettings = async () => {
    await api.request({
      url: '/attributes/local/update-from-global',
      method: 'POST',
      data: { snapshot: props.snapshotId },
    });
    props.onUpdated?.();
    reloadTableDataRef.current?.();
  };

  const confirmUpdateFromGlobal = async () => {
    const needsRecalculation = await willGlobalUpdateNeedRecalculation();
    return confirmUpdateFromGlobalDialog.show({
      btnConfirm: 'Overwrite',
      btnSecondaryConfirm: 'Cancel',
      header: 'Overwrite attributes from global settings?',
      subHeader: (
        <>
          <p>
            Do you want to overwrite the local device attributes in this
            snapshot with the ones from the global settings?
          </p>
          {needsRecalculation && (
            <p>
              <strong>Note:</strong> Graph recalculation will start. This
              process can take some time.
            </p>
          )}
        </>
      ),
    });
  };

  const handleUpdateButtonClick = () =>
    withLoadingIndicator(setUpdatingFromGlobal)(async () => {
      if (await confirmUpdateFromGlobal()) {
        await updateFromGlobalSettings();
      }
    });

  const { actionItem } = useResponsiveTableButton({
    label: 'Overwrite with global settings',
    icon: 'fa-arrows-rotate',
    onClick: handleUpdateButtonClick,
    containerRef: props.containerRef,
    size: 'small',
    loading: updatingFromGlobal === 'LOADING',
  });

  const menuItems = useMemo(() => [actionItem], [actionItem]);

  const deviceAttributesEndpoint = useTableEndpoint({
    apiEndpoint: '/tables/snapshot-attributes',
    alternative: (originalEndpoint) => ({
      ...originalEndpoint,
      title: 'List of attributes',
    }),
  });

  const apiTableModel = useApiTable({
    endpoint: deviceAttributesEndpoint,
    menuItems,
    ...props,
  });

  useEffect(() => {
    reloadTableDataRef.current = () => {
      apiTableModel.dispatch(reloadTableData);
    };
  }, [apiTableModel.dispatch]);

  return {
    model: apiTableModel,
    renderConfirmDialog: confirmUpdateFromGlobalDialog.render,
  };
};
